/**
 * nj-tickets-v2 — the event designer's Tickets step on the Tickets v2 module (CC 0195,
 * operator ruling 5 Oct). Events → Tickets is THE tickets screen; the old
 * ticket-types / ticket-groups stores are not read or written from the designer.
 *
 * Operations (all through tickets-v2-api's tv2Call, so an unregistered path throws):
 *   listTicketGroupsV2       GET  /v1/admin/ticket-group-library
 *   getTicketGroupVersion    GET  /v1/admin/ticket-groups/{id}/versions/{version}
 *   getEventTicketing        GET  /v1/admin/events/{eventId}/ticketing
 *   applyTicketGroupToEvent  POST /v1/admin/events/{eventId}/ticketing/apply-group
 *
 * An event has ONE ticket group in v2 (its tiers are copied onto the event), and
 * capacity is the sum of the tiers' seats, server-computed.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { tv2Call } from "../tickets/v2/tickets-v2-api";

export interface V2Group { id: string; name: string; description: string; latestVersion: number | null; eventCount: number }
/** A tier in the designer's display shape (the old step's: kind/price in major units/allocation). */
export interface V2TierView { id: string; name: string; kind: "free" | "paid"; price: number; priceMinor: number; defaultAllocation: number; color?: string; sold?: number; waitlisted?: number }
export interface V2EventTicketing { ticketGroupId: string | null; ticketGroupVersion: number | null; newerGroupVersion: number | null; currency: string; capacity: number; tiers: V2TierView[] }

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);

function tierView(t: any): V2TierView {
  const minor = num(t?.priceMinor);
  return {
    id: String(t?.id ?? ""), name: String(t?.name ?? ""), color: t?.color,
    kind: minor === 0 ? "free" : "paid", priceMinor: minor, price: minor / 100,
    defaultAllocation: num(t?.seats),
    sold: t?.availability ? num(t.availability.sold) : undefined,
    waitlisted: t?.availability ? num(t.availability.waitlisted) : undefined,
  };
}
const live = (t: any) => !t?.status || t.status !== "retired";

/** Money from major units in the given currency (replaces the old store's tgMoney). */
export function v2Money(major: number, currency = "USD"): string {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(major); }
  catch { return `${major.toFixed(2)} ${currency}`; }
}

export async function listV2Groups(): Promise<V2Group[]> {
  const r: any = await tv2Call("GET", "/v1/admin/ticket-group-library");
  const rows: any[] = r?.groups ?? r?.data ?? [];
  return rows.map((g) => ({
    id: String(g.id), name: String(g.name ?? ""), description: String(g.description ?? ""),
    latestVersion: g.latestVersion == null ? null : num(g.latestVersion), eventCount: num(g.eventCount),
  }));
}

export async function getV2GroupTiers(groupId: string, version: number): Promise<V2TierView[]> {
  const r: any = await tv2Call("GET", `/v1/admin/ticket-groups/${encodeURIComponent(groupId)}/versions/${version}`);
  return ((r?.version?.tiers ?? []) as any[]).filter(live).map(tierView);
}

export async function getV2EventTicketing(eventId: string): Promise<V2EventTicketing> {
  const r: any = await tv2Call("GET", `/v1/admin/events/${encodeURIComponent(eventId)}/ticketing`);
  const t = r?.ticketing ?? {};
  return {
    ticketGroupId: t.ticketGroupId ?? null, ticketGroupVersion: t.ticketGroupVersion ?? null,
    newerGroupVersion: t.newerGroupVersion ?? null, currency: String(t.currency || "USD"),
    capacity: num(t.capacity), tiers: ((t.tiers ?? []) as any[]).filter(live).map(tierView),
  };
}

/** The new module's apply path, the one Event tickets uses (copies the group's tiers onto the event). */
export async function applyV2GroupToEvent(eventId: string, ticketGroupId: string): Promise<V2EventTicketing> {
  await tv2Call("POST", `/v1/admin/events/${encodeURIComponent(eventId)}/ticketing/apply-group`, { ticketGroupId });
  return getV2EventTicketing(eventId);
}

/** Library + the selected group's tiers + (for an existing event) its ticketing. */
export function useV2Tickets(eventId: string | null, selectedGroupId: string | null) {
  const [groups, setGroups] = useState<V2Group[] | null>(null);
  const [groupsErr, setGroupsErr] = useState("");
  const [tiers, setTiers] = useState<Record<string, V2TierView[]>>({});
  const [ticketing, setTicketing] = useState<V2EventTicketing | null>(null);
  const [ticketingErr, setTicketingErr] = useState("");
  const [applying, setApplying] = useState(false);
  const evRef = useRef(eventId); evRef.current = eventId;

  const loadGroups = useCallback(() => {
    setGroupsErr("");
    listV2Groups().then((g) => setGroups(g), (e) => { setGroups([]); setGroupsErr(e instanceof Error ? e.message : String(e)); });
  }, []);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const loadTicketing = useCallback(() => {
    if (!eventId) { setTicketing(null); return; }
    const forEv = eventId; setTicketingErr("");
    getV2EventTicketing(forEv).then((t) => { if (evRef.current === forEv) setTicketing(t); }, (e) => { if (evRef.current === forEv) setTicketingErr(e instanceof Error ? e.message : String(e)); });
  }, [eventId]);
  useEffect(() => { loadTicketing(); }, [loadTicketing]);

  useEffect(() => {
    const g = selectedGroupId ? groups?.find((x) => x.id === selectedGroupId) : null;
    if (!g || !g.latestVersion || tiers[g.id]) return;
    getV2GroupTiers(g.id, g.latestVersion).then((t) => setTiers((m) => ({ ...m, [g.id]: t })), () => setTiers((m) => ({ ...m, [g.id]: [] })));
  }, [selectedGroupId, groups, tiers]);

  const apply = useCallback(async (forEventId: string, groupId: string) => {
    setApplying(true);
    try { const t = await applyV2GroupToEvent(forEventId, groupId); if (evRef.current === forEventId) setTicketing(t); return t; }
    finally { setApplying(false); }
  }, []);

  return { groups, groupsErr, reloadGroups: loadGroups, tiersByGroup: tiers, ticketing, ticketingErr, reloadTicketing: loadTicketing, apply, applying };
}
